import { Droplets } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatModelNumber } from './modelInputContract';
import {
  DEFAULT_DILUTION_SETUP,
  DILUTION_CHALLENGES,
  DILUTION_FIELDS,
  DILUTION_QUESTION,
  type DilutionSetup,
  calculateDilutionExplore,
  sig3,
} from './solutionsExplore';

/** A glassware sketch whose dots show relative concentration. `dots` overrides the default count. */
export function ConcentrationVessel({
  label,
  concentration,
  volume,
  dilute,
  dots,
}: {
  label: string;
  concentration: number;
  volume: number;
  dilute?: boolean;
  dots?: number;
}) {
  const dotCount = dots ?? Math.max(4, Math.min(24, Math.round(concentration * 10)));
  return (
    <div className="text-center">
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">{label}</p>
      <div
        className={`relative mx-auto mt-3 h-56 overflow-hidden border-4 border-brand-sky/25 bg-white ${
          dilute ? 'w-44 rounded-b-[3rem] rounded-t-3xl' : 'w-28 rounded-b-3xl rounded-t-xl'
        }`}
        role="img"
        aria-label={`${label}: ${volume} millilitres at ${concentration.toFixed(3)} moles per litre`}
        data-dots={dotCount}
      >
        <div className="absolute inset-x-1 bottom-1 h-[76%] rounded-b-[2.5rem] bg-brand-sky/25" />
        <div className="absolute inset-x-3 bottom-4 grid grid-cols-4 content-around justify-items-center gap-2">
          {Array.from({ length: dotCount }, (_, index) => (
            <span
              key={index}
              className="h-3 w-3 rounded-full border border-brand-coral bg-brand-coral"
              aria-hidden="true"
            />
          ))}
        </div>
        {dilute ? (
          <div className="absolute inset-x-0 top-[23%] border-t-2 border-dashed border-ink/45" />
        ) : null}
      </div>
      <p className="mt-3 text-sm font-extrabold text-ink">{volume} mL</p>
      <p className="text-xs font-bold text-brand-sky">{concentration.toFixed(3)} mol L⁻¹</p>
    </div>
  );
}

const STOCK_DOTS = 20;

const DILUTION_PRESETS: { id: string; label: string; setup: DilutionSetup }[] = [
  { id: 'ten-times', label: '1.00 mol/L, 25 mL to 250 mL', setup: DEFAULT_DILUTION_SETUP },
  { id: 'one-in-five', label: '0.500 mol/L, 20 mL to 100 mL', setup: { stockConcentrationMolL: 0.5, aliquotVolumeMl: 20, finalVolumeMl: 100 } },
  { id: 'large-flask', label: '2.00 mol/L, 10 mL to 1000 mL', setup: { stockConcentrationMolL: 2, aliquotVolumeMl: 10, finalVolumeMl: 1000 } },
];

/**
 * Senior Explore: the learner types C₁, V₁ and V₂. The vessels, readouts and the worked
 * C₂ = C₁ × V₁ ÷ V₂ calculation all come from `calculateDilutionExplore`. Invalid input keeps the
 * last valid setup and says why.
 */
export function DilutionExplore() {
  const [setup, setSetup] = useState<DilutionSetup>(DEFAULT_DILUTION_SETUP);
  const [validSetup, setValidSetup] = useState<DilutionSetup>(DEFAULT_DILUTION_SETUP);
  const [setupError, setSetupError] = useState<string | null>(null);
  const result = useMemo(() => calculateDilutionExplore(validSetup), [validSetup]);

  const apply = (next: DilutionSetup) => {
    setSetup(next);
    try {
      calculateDilutionExplore(next);
      setValidSetup(next);
      setSetupError(null);
    } catch (error) {
      setSetupError(error instanceof Error ? error.message : String(error));
    }
  };

  const finalDots = Math.max(1, Math.round((STOCK_DOTS * result.finalConcentrationMolL) / validSetup.stockConcentrationMolL));

  return (
    <article className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Explore dilution</p>
      <h3 className="mt-2 text-2xl font-extrabold text-ink">Dilute your own stock solution</h3>
      <p className="mt-1 text-sm font-medium text-slate2">
        Type the stock concentration, the aliquot you pipette and the volumetric flask you fill to the mark. The model
        assumes ideal glassware and complete mixing.
      </p>

      <div className="mt-5 grid min-w-0 gap-5 lg:grid-cols-2">
        <div className="min-w-0">
          <div>
            <ModelInputPanel
              legend="Set up the dilution"
              fields={DILUTION_FIELDS}
              values={setup}
              onChange={(next) => apply(next as DilutionSetup)}
              sliderKey="finalVolumeMl"
            />
          </div>
          {setupError ? (
            <p role="alert" className="mt-2 rounded-2xl bg-wash-coral px-3 py-2 text-xs font-bold text-ink">
              {setupError} Showing the last valid setup.
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Dilution presets">
            {DILUTION_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => apply(preset.setup)}
                className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <ExploreChallenges
              question={DILUTION_QUESTION}
              challenges={DILUTION_CHALLENGES}
              result={{ setup: validSetup, result }}
            />
          </div>

          <div className="mt-4 grid min-w-0 grid-cols-1 items-center gap-4 rounded-[1.75rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-3">
            <ConcentrationVessel
              label="Measured aliquot"
              concentration={validSetup.stockConcentrationMolL}
              volume={validSetup.aliquotVolumeMl}
              dots={STOCK_DOTS}
            />
            <div className="flex flex-col items-center gap-2 text-brand-sky">
              <Droplets className="h-8 w-8" aria-hidden="true" />
              <span className="text-xs font-extrabold">add {formatModelNumber(result.waterAddedMl, 1)} mL water</span>
            </div>
            <ConcentrationVessel
              label="Volumetric flask"
              concentration={result.finalConcentrationMolL}
              volume={validSetup.finalVolumeMl}
              dilute
              dots={finalDots}
            />
          </div>
          <p className="mt-2 text-xs font-medium leading-5 text-slate2">
            Dots show concentration relative to the stock (same area of liquid), not the real number of particles.
          </p>
        </div>

        <div className="min-w-0">
          <WorkedStepsPanel
            title="How the model works it out"
            lead={result.lead}
            leadTestId="dilution-factor"
            steps={result.steps}
            stepTestIdPrefix="dilution-step"
            result={result.result}
            resultTestId="dilution-result"
            limitNote={result.limitNote}
          />
          <dl className="mt-4 grid gap-3 sm:grid-cols-3" aria-label="Dilution readout">
            <div className="rounded-2xl border border-hairline bg-surface p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Moles transferred</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink">{sig3(result.molesTransferred)} mol</dd>
            </div>
            <div className="rounded-2xl border border-hairline bg-surface p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Water added</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink">{formatModelNumber(result.waterAddedMl, 1)} mL</dd>
            </div>
            <div className="rounded-2xl border border-hairline bg-surface p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Final concentration</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink">{sig3(result.finalConcentrationMolL)} mol/L</dd>
            </div>
          </dl>
        </div>
      </div>
    </article>
  );
}
